import ApiServiceBase from "@/services/api/apiServiceBase";
import {Preferences} from "@capacitor/preferences";
import {PreferenceKey} from "@/definitions/PreferenceKey";
import {UserInterface} from "@/interfaces/UserInterface";
import {StatusInterface} from "@/interfaces/StatusInterface";
import {UserViewerInterface} from "@/interfaces/UserViewerInterface";
import {ReviewInterface} from "@/interfaces/ReviewInterface";
import {dataUrlToBlob, normalizeFriendRequest, normalizeListedUser, normalizePaginated, normalizeProfile} from "@/services/api/normalizeProfile";
import {normalizeFeedPosts} from "@/services/api/normalizeFeed";

const normalizeAuthUser = (user: UserInterface): UserInterface => ({
    ...user,
    app_reviewer: Boolean(user?.app_reviewer),
});

function asPaginatedLegacy(payload: any) {
    const {data, pagination} = normalizePaginated(payload);
    return {
        data,
        meta: {
            current_page: pagination.page ?? 1,
            last_page: pagination.totalPages ?? 1,
            per_page: pagination.limit ?? 20,
            total: pagination.total ?? data.length,
        },
        pagination,
    };
}

function asFeedPaginatedLegacy(payload: any) {
    const page = asPaginatedLegacy(payload);
    return {...page, data: normalizeFeedPosts(page.data)};
}

function requestIdFrom(idOrRequest: number | {id?: number; requestId?: number}): number {
    if (typeof idOrRequest === 'object' && idOrRequest != null) {
        return Number(idOrRequest.requestId ?? idOrRequest.id);
    }
    return Number(idOrRequest);
}

export class UserService extends ApiServiceBase {

    public async authUser(): Promise<UserInterface> {
        const user = (await Preferences.get({key: PreferenceKey.AuthUser})).value;
        if (!user) return this.get();
        return normalizeAuthUser(JSON.parse(user as string) as UserInterface);
    }

    public async delete(): Promise<any> {
        return this.http?.delete('/profile', {});
    }

    public async get(): Promise<UserInterface> {
        const envelope = await this.http?.get('/profile');
        let preferences: Record<string, any> = {};
        try {
            preferences = (await this.http?.get('/user/preferences')) || {};
        } catch {
            preferences = {};
        }
        const user = normalizeAuthUser(normalizeProfile(envelope, preferences));
        await Preferences.set({key: PreferenceKey.AuthUser, value: JSON.stringify(user)});
        if (user?.id) {
            await Preferences.set({key: PreferenceKey.UserId, value: String(user.id)});
        }
        return user;
    }

    public async getById(id: number | string, _withPreferences = false): Promise<UserInterface> {
        const envelope = await this.http?.get(`/users/${id}`);
        return normalizeProfile(envelope);
    }

    public async posts(id: number, page = 1): Promise<any> {
        return asFeedPaginatedLegacy(await this.http?.get('/feeds', {userId: id, page}));
    }

    public async reviews(userId: number, page = 1): Promise<any> {
        const auth = await this.authUser().catch(() => null);
        if (auth && Number(auth.id) === Number(userId)) {
            return asPaginatedLegacy(await this.http?.get('/reviews/user', {page}));
        }
        const profile: any = await this.getById(userId);
        const list = profile?.userReviews || profile?.reviews || [];
        const items = Array.isArray(list) ? list : [];
        return {
            data: items,
            meta: {current_page: 1, last_page: 1, per_page: items.length, total: items.length},
        };
    }

    public async reviewForUserId(userId: number): Promise<any> {
        const profile: any = await this.getById(userId);
        const own = profile?.authUserReviews;
        if (Array.isArray(own) && own.length) return own[0];
        return own ?? null;
    }

    public async addReview(revieweeId: number, review: string): Promise<any> {
        return this.http?.post('/reviews', {userId: Number(revieweeId), text: review});
    }

    public async getReviewById(id: number): Promise<any> {
        const written = normalizePaginated(await this.http?.get('/reviews')).data;
        return written.find((r: any) => Number(r.id) === Number(id)) ?? null;
    }

    public async updateReview(review: ReviewInterface): Promise<any> {
        return this.http?.put(`/reviews/${review.id}`, {text: review.text});
    }

    public async deleteReview(review: ReviewInterface): Promise<any> {
        return this.http?.delete(`/reviews/${review.id}`, {});
    }

    public async reportReview(_id: number): Promise<any> {
        return Promise.reject({
            success: false,
            message: 'Reporting reviews is not supported',
            error: 'NOT_SUPPORTED',
        });
    }

    public async gallery(page = 1): Promise<any> {
        return asPaginatedLegacy(await this.http?.get('/profile/gallery', {page, limit: 30}));
    }

    public async galleryByUserId(userId: number, page = 1): Promise<any> {
        return asPaginatedLegacy(await this.http?.get(`/users/gallery/${userId}`, {page, limit: 10}));
    }

    public async updateGalleryMedia(images: string[] = []): Promise<any> {
        const form = new FormData();
        for (let i = 0; i < images.length; i++) {
            const blob = await dataUrlToBlob(images[i]);
            form.append('images', blob, `gallery-${i}.jpg`);
        }
        return this.http?.upload('/profile/gallery', form);
    }

    public async getVerificationStatus(): Promise<StatusInterface> {
        const user: any = await this.get();
        const verified = Boolean(user?.id_verified);
        const pending = Boolean(user?.hasPendingVerificationVideo);
        return {
            id_card_photo: null,
            id_photo: null,
            submitted_at: pending ? new Date().toISOString() : null,
            verified_at: verified ? new Date().toISOString() : null,
        };
    }

    public async submitIdForVerification(): Promise<any> {
        return this.get();
    }

    public async cancelIdVerification(): Promise<any> {
        return Promise.resolve(null);
    }

    public async setPreference(key: string, value: any): Promise<any> {
        return this.http?.post('/user/preferences', {
            preferences: {[key]: value},
        });
    }

    public async createProfile(): Promise<any> {
        return this.http?.post('/profile', {});
    }

    public async deleteProfile(_profileId: number): Promise<any> {
        return Promise.resolve(null);
    }

    public async setName(name: string): Promise<any> {
        await this.http?.post('/profile', {name});
        return this.get();
    }

    public async setProfileName(_profileId: number, name: string): Promise<any> {
        return this.setName(name);
    }

    public async setGender(_profileId: number, id: number): Promise<any> {
        await this.http?.post('/profile', {genderId: Number(id)});
        return this.get();
    }

    public async setSexuality(_profileId: number, id: number): Promise<any> {
        await this.http?.post('/profile', {sexualityId: Number(id)});
        return this.get();
    }

    public async setSexualInterests(id: string | number | number[]): Promise<any> {
        const sexualInterests = Array.isArray(id)
            ? id.map(Number)
            : String(id).split(',').map((v) => Number(v.trim())).filter((n) => !Number.isNaN(n) && n > 0);
        await this.http?.post('/profile', {sexualInterests});
        return this.get();
    }

    public async setDesires(id: string | number | number[]): Promise<any> {
        const desires = Array.isArray(id)
            ? id.map(Number)
            : String(id).split(',').map((v) => Number(v.trim())).filter((n) => !Number.isNaN(n) && n > 0);
        await this.http?.post('/profile', {desires});
        return this.get();
    }

    public async setDob(_profileId: number, dob: string): Promise<any> {
        await this.http?.post('/profile', {dob});
        return this.get();
    }

    public async setBio(bio: string): Promise<any> {
        await this.http?.post('/profile', {bio});
        return this.get();
    }

    public async setSubscriberId(): Promise<any> {
        let userId: any = (await Preferences.get({key: PreferenceKey.UserId})).value;
        if (!userId) userId = (await this.get()).id;
        return userId;
    }

    public async getPreference(key: string, _userId?: number): Promise<any> {
        const prefs = await this.http?.get('/user/preferences');
        return prefs ? prefs[key] : null;
    }

    public async getPreferences(keys?: string[], _userId?: number): Promise<any> {
        const prefs = await this.http?.get('/user/preferences');
        if (!keys?.length) return prefs;
        const filtered: Record<string, any> = {};
        for (const key of keys) {
            if (prefs && key in prefs) filtered[key] = prefs[key];
        }
        return filtered;
    }

    public async sendEmailVerification(): Promise<boolean> {
        const user = await this.authUser();
        if (!user?.email) return false;
        await this.http?.post('/auth/resend_verification', {email: user.email}, false);
        return true;
    }

    public async registerDeviceToken(token: string): Promise<unknown> {
        await this.http?.post('/settings/fcm-token', {fcmToken: token});
        await Preferences.set({key: PreferenceKey.DeviceToken, value: `${token}`});
        return token;
    }

    public async revokeDeviceToken(): Promise<unknown> {
        await this.http?.post('/settings/fcm-token', {fcmToken: null});
        await Preferences.remove({key: PreferenceKey.DeviceToken});
        return null;
    }

    private async uploadProfilePhotoField(field: string, image: string): Promise<UserInterface> {
        const form = new FormData();
        const blob = await dataUrlToBlob(image);
        form.append(field, blob, `${field}.jpg`);
        await this.http?.upload('/profile/gallery', form);
        return this.get();
    }

    public async setProfilePhoto(image: string): Promise<UserInterface> {
        return this.uploadProfilePhotoField('profilePhoto', image);
    }

    public async setCoverPhoto(image: string): Promise<UserInterface> {
        return this.uploadProfilePhotoField('coverPhoto', image);
    }

    public async setIdCardPhoto(image: string): Promise<UserInterface> {
        return this.uploadProfilePhotoField('idPhoto', image);
    }

    public async setIdPhoto(image: string): Promise<UserInterface> {
        return this.uploadProfilePhotoField('idPhoto', image);
    }

    public async changePassword(password: string, password_confirmation: string) {
        await this.http?.post('/auth/change_password', {password, password_confirmation});
        return this.get();
    }

    public async getNotifications(page = 1): Promise<any> {
        return asPaginatedLegacy(await this.http?.get('/notifications', {page, limit: 10}));
    }

    public async getNotificationById(id: number): Promise<any> {
        const {data} = asPaginatedLegacy(await this.http?.get('/notifications', {page: 1, limit: 50}));
        return data.find((n: any) => Number(n.id) === Number(id)) ?? null;
    }

    public async getUnseenNotifications(): Promise<any> {
        const payload = await this.http?.get('/notifications/unseen');
        if (Array.isArray(payload)) return payload;
        if (payload?.data && Array.isArray(payload.data)) return payload.data;
        return payload ?? [];
    }

    public async deleteAllNotifications(): Promise<any> {
        return this.http?.delete('/notifications/all', {});
    }

    public async seenNotifications(ids: string | number | number[]): Promise<any> {
        const list = Array.isArray(ids)
            ? ids
            : String(ids).split(',').map((v) => Number(v.trim())).filter((n) => !Number.isNaN(n));
        let last: any = null;
        for (const notificationId of list) {
            last = await this.http?.post('/notifications/mark_seen', {notificationId: Number(notificationId)});
        }
        return last;
    }

    public async deleteNotification(notificationId: number): Promise<any> {
        return this.http?.delete('/notifications', {notificationId: Number(notificationId)});
    }

    public async getFriend(id: number): Promise<any> {
        return this.getById(id);
    }

    public async getFriends(page = 1, limit = 10): Promise<any> {
        const result = normalizePaginated(await this.http?.get('/friends', {page, limit}));
        return result.data.map((row: any) => normalizeListedUser(row));
    }

    public async getFriendsForUser(id: number, page = 1, limit = 10): Promise<any> {
        const result = normalizePaginated(await this.http?.get('/friends', {userId: id, page, limit}));
        return result.data;
    }

    public async getFollowingForUserId(id: number, page = 1, limit = 10): Promise<any> {
        const result = normalizePaginated(await this.http?.get('/followings', {userId: id, page, limit}));
        return result.data;
    }

    public async getFollowersForUserId(id: number, page = 1, limit = 10): Promise<any> {
        const result = normalizePaginated(await this.http?.get('/followers', {userId: id, page, limit}));
        return result.data;
    }

    public async getFollowing(page = 1, limit = 10): Promise<any> {
        const result = normalizePaginated(await this.http?.get('/followings', {page, limit}));
        return result.data;
    }

    public async getFollowers(page = 1, limit = 10): Promise<any> {
        const result = normalizePaginated(await this.http?.get('/followers', {page, limit}));
        return result.data;
    }

    public async getWinksForUser(id: number, page = 1, limit = 10): Promise<any> {
        const result = normalizePaginated(await this.http?.get('/winks', {userId: id, page, limit}));
        return result.data;
    }

    public async getWinks(page = 1, limit = 10): Promise<any> {
        const result = normalizePaginated(await this.http?.get('/winks', {page, limit}));
        return result.data;
    }

    public async getFriendRequests(): Promise<any> {
        const payload = await this.http?.get('/friends/requests');
        const list = Array.isArray(payload) ? payload : (payload?.data ?? []);
        if (!Array.isArray(list)) return [];
        return list.map((item: any) => normalizeFriendRequest(item)).filter(Boolean);
    }

    public async getFriendRequestsSent(): Promise<any> {
        const payload = await this.http?.get('/friends/pendings');
        return Array.isArray(payload) ? payload : (payload?.data ?? payload ?? []);
    }

    public async makeFriendRequest(id: number): Promise<any> {
        return this.http?.post('/friends', {userId: Number(id)});
    }

    /** Accept uses requestId from GET /friends/requests (not the peer user id). */
    public async acceptFriendRequest(idOrRequest: number | {id?: number; requestId?: number}): Promise<any> {
        return this.http?.post('/friends/accept', {requestId: requestIdFrom(idOrRequest)});
    }

    public async rejectFriendRequest(idOrRequest: number | {id?: number; requestId?: number}): Promise<any> {
        return this.http?.post('/friends/reject', {requestId: requestIdFrom(idOrRequest)});
    }

    public async removeFromFriends(id: number): Promise<any> {
        return this.http?.delete(`/friends/${id}`, {});
    }

    public async getPartner(id: number): Promise<any> {
        return this.getById(id);
    }

    public async getPartners(): Promise<any> {
        const payload = await this.http?.get('/partners');
        return Array.isArray(payload) ? payload : (payload?.data ?? payload ?? []);
    }

    public async getPartnersForUser(_id: number): Promise<any> {
        return this.getPartners();
    }

    public async getPartnerRequests(): Promise<any> {
        const payload = await this.http?.get('/partners/requests');
        return Array.isArray(payload) ? payload : (payload?.data ?? payload ?? []);
    }

    public async getPartnerRequestsSent(): Promise<any> {
        return [];
    }

    public async makePartnerRequest(id: number): Promise<any> {
        return this.http?.post('/partners', {userId: Number(id)});
    }

    public async acceptPartnerRequest(idOrRequest: number | {id?: number; requestId?: number}): Promise<any> {
        return this.http?.post('/partners/accept', {requestId: requestIdFrom(idOrRequest)});
    }

    public async rejectPartnerRequest(idOrRequest: number | {id?: number; requestId?: number}): Promise<any> {
        return this.http?.post('/partners/reject', {requestId: requestIdFrom(idOrRequest)});
    }

    public async removeFromPartners(id: number): Promise<any> {
        return this.http?.delete(`/partners/${id}`, {});
    }

    public async reportUser(id: number, reason: string): Promise<any> {
        return this.http?.post('/reports/users', {userId: Number(id), reason});
    }

    public async blockUser(id: number): Promise<any> {
        return this.http?.post('/users/block', {blockedUserId: Number(id)});
    }

    public async unblockUser(id: number): Promise<any> {
        return this.http?.post('/users/unblock', {blockedUserId: Number(id)});
    }

    public async blocked(): Promise<any> {
        const payload = await this.http?.get('/users_blocked');
        return Array.isArray(payload) ? payload : (payload?.data ?? payload ?? []);
    }

    public async winkAtUser(userId: number): Promise<any> {
        return this.http?.post('/winks', {userId: Number(userId)});
    }

    public async reportAndBlock(id: number, reason: string): Promise<any> {
        await this.reportUser(id, reason);
        await this.blockUser(id);
        return true;
    }

    public async setPronouns(_profileId: number, id: number | null): Promise<any> {
        if (typeof id === 'number') {
            await this.http?.post('/profile', {pronounId: id});
        }
        return this.get();
    }

    public async setReligion(_profileId: number, id: number | null): Promise<any> {
        if (typeof id === 'number') {
            await this.http?.post('/profile', {religionId: id});
        }
        return this.get();
    }

    public async setBodyType(_profileId: number, id: number | null): Promise<any> {
        if (typeof id === 'number') {
            await this.http?.post('/profile', {bodyTypeId: id});
        }
        return this.get();
    }

    public async setDrinkingType(_profileId: number, id: number | null): Promise<any> {
        if (typeof id === 'number') {
            await this.http?.post('/profile', {drinkingTypeId: id});
        }
        return this.get();
    }

    public async setSmokingType(_profileId: number, id: number | null): Promise<any> {
        if (typeof id === 'number') {
            await this.http?.post('/profile', {smokingTypeId: id});
        }
        return this.get();
    }

    public async setHeight(_profileId: number, heightInCM: number | null): Promise<any> {
        if (typeof heightInCM === 'number') {
            await this.http?.post('/profile', {heightInCm: heightInCM});
        }
        return this.get();
    }

    public async setPiercings(_profileId: number, value: boolean | null): Promise<any> {
        if (typeof value === 'boolean') {
            await this.http?.post('/profile', {hasPiercings: value});
        }
        return this.get();
    }

    public async setTattoos(_profileId: number, value: boolean | null): Promise<any> {
        if (typeof value === 'boolean') {
            await this.http?.post('/profile', {hasTattoos: value});
        }
        return this.get();
    }

    public async setLocation(location: any): Promise<any> {
        const body: Record<string, any> = {};
        if (location?.address != null) body.address = location.address;
        if (location?.city != null) body.city = location.city;
        if (location?.state != null) body.state = location.state;
        if (location?.country != null) body.country = location.country;
        if (location?.zip != null) body.zip = location.zip;
        if (location?.lat != null) body.lat = Number(location.lat);
        if (location?.lng != null) body.lng = Number(location.lng);
        if (location?.name && !body.address) body.address = location.name;
        await this.http?.post('/profile', body);
        return this.get();
    }

    public async reportProblem(problem: string): Promise<any> {
        return this.http?.post('/problems', {title: problem});
    }

    public async revokeVerification(): Promise<any> {
        return Promise.resolve(null);
    }

    public async updateCurrentPosition(lat: number, lng: number): Promise<any> {
        return this.http?.post('/profile', {lat: Number(lat), lng: Number(lng)});
    }

    public async views(page = 1, limit = 20): Promise<UserViewerInterface[]> {
        const result = normalizePaginated(await this.http?.get('/profile/views', {page, limit}));
        return result.data as UserViewerInterface[];
    }

    public async followUser(id: number): Promise<any> {
        return this.http?.post('/followings', {userId: Number(id)});
    }

    public async unfollowUser(id: number): Promise<any> {
        return this.http?.delete(`/followings/${id}`, {});
    }

    public async acceptFollowRequest(idOrRequest: number | {id?: number; requestId?: number}): Promise<any> {
        return this.http?.post('/followings/accept', {requestId: requestIdFrom(idOrRequest)});
    }

    public async rejectFollowRequest(idOrRequest: number | {id?: number; requestId?: number}): Promise<any> {
        return this.http?.post('/followings/reject', {requestId: requestIdFrom(idOrRequest)});
    }

    public async getFollowRequests(): Promise<any> {
        const payload = await this.http?.get('/followings/requests');
        return Array.isArray(payload) ? payload : (payload?.data ?? payload ?? []);
    }

    /**
     * @param duration Hours to remain available (UI MeetSheet). 0 clears availability.
     */
    public async setAvailability(duration: number): Promise<any> {
        const hours = Number(duration);
        const availableToMeetUntil = hours > 0
            ? new Date(Date.now() + hours * 60 * 60 * 1000).toISOString()
            : null;
        await this.http?.post('/profile', {availableToMeetUntil});
        return this.get();
    }

    public async availableToMeet(params?: {lat?: number; lng?: number; radius?: number}): Promise<any> {
        let lat = params?.lat;
        let lng = params?.lng;
        if (lat == null || lng == null) {
            const user = await this.authUser().catch(() => null);
            lat = lat ?? Number(user?.lat ?? user?.location?.lat);
            lng = lng ?? Number(user?.lng ?? user?.location?.lng);
        }
        if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) {
            return [];
        }
        const result = normalizePaginated(await this.http?.get('/users/available', {
            lat,
            lng,
            radius: params?.radius ?? 100,
        }));
        return result.data;
    }

    public async attachVerificationVideo(video: File | Blob, onProgress?: (progress: number) => void): Promise<any> {
        const form = new FormData();

        if (video instanceof File) {
            form.append('verification_video', video, video.name);
        } else {
            const mimeType = (video.type || 'video/webm').split(';')[0];
            const extension = mimeType.includes('mp4') ? 'mp4' : 'webm';
            form.append(
                'verification_video',
                new File([video], `verification.${extension}`, {type: mimeType}),
            );
        }

        return this.http?.upload('/profile/verification', form, (progress: number) => {
            onProgress?.(progress);
        });
    }

    public async sleep(): Promise<any> {
        return this.http?.put('/users/sleep', {});
    }

    public async wake(): Promise<any> {
        return this.http?.put('/users/wake', {});
    }

    public async createPrivacyPin(pin: string): Promise<any> {
        return this.http?.post('/profile/privacy-pin/create', {pin});
    }

    public async verifyPrivacyPin(pin: string): Promise<any> {
        return this.http?.post('/profile/privacy-pin/verify', {pin});
    }

    public async removePrivacyPin(): Promise<any> {
        return this.http?.delete('/profile/privacy-pin/remove', {});
    }

    public async resetPrivacyPin(password: string): Promise<any> {
        return this.http?.post('/profile/privacy-pin/reset', {password});
    }
}
